import React from 'react';
import {useCallback, useMemo, useRef, useState} from 'react';
import {Button, Table as STable, Popup} from 'semantic-ui-react';
import * as Panel2 from './panel';
import makeComp from '../../util/profiler';
import * as SemanticHacks from '../../util/semanticHacks';
import * as CG from './cg/graph';
import * as HL from './cg/hl';
import {PanelComp2} from './PanelComp';
import * as LLReact from './cg/cgreact';
import * as ExpressionView from './ExpressionView';
import ModifiedDropdown from '../elements/ModifiedDropdown';
import * as ExpressionEditor from './ExpressionEditor';
import EditableField from '../EditableField';
import * as Types from './types';
import * as Op from './cg/ops';
import * as Table from './tableState';
import * as WBMenu from '../elements/WBMenu';
import * as S from './PanelTable2.styles';
import {ControlFilter} from './ControlFilter';
import WBPopupMenuTrigger from '../elements/WBPopupMenuTrigger';
import PageControls from './ControlPage';
import {useWhenOnScreenAfterNewValue, useGatedValue} from '../../state/hooks';
import {getPanelStacksForType, getPanelStackDims} from './availablePanels';
// import {useTraceUpdate} from '../../util/hooks';

const inputType = {type: 'array' as const, objectType: 'any' as const};

export const ColumnHeader: React.FC<{
  isGroupCol: boolean;
  tableState: Table.TableState;
  inputArrayNode: CG.Node;
  exampleRowNode: CG.Node;
  columnName: string;
  selectFunction: CG.NodeOrVoidNode;
  filterFunction: CG.NodeOrVoidNode;
  colId: string;
  panelId: string;
  config: any;
  panelContext: any;
  updateConfig(newConfig: any): void;
  updatePanelContext(newContext: any): void;
  updateTableState(newTableState: Table.TableState): void;
  updateColumnName(newColumnName: string): void;
  updateSelectFunction(newNode: CG.NodeOrVoidNode): void;
  updateFilterFunction(newNode: CG.NodeOrVoidNode): void;
  updatePanelId(newPanelId: string): void;
}> = makeComp(
  ({
    isGroupCol,
    tableState,
    inputArrayNode,
    exampleRowNode,
    columnName,
    selectFunction: propsSelectFunction,
    filterFunction: propsFilterFunction,
    colId,
    panelId,
    config,
    panelContext,
    updateConfig,
    updatePanelContext,
    updateColumnName,
    updateSelectFunction: propsUpdateSelectFunction,
    updateFilterFunction: propsUpdateFilterFunction,
    updatePanelId,
    updateTableState,
  }) => {
    const [selectFunction, setSelectFunction] = useState<HL.EditingNode>(
      propsSelectFunction
    );
    const updateSelectFunction = useCallback(() => {
      if (HL.nodeIsExecutable(selectFunction)) {
        propsUpdateSelectFunction(selectFunction);
      }
    }, [propsUpdateSelectFunction, selectFunction]);

    const [filterFunction, setFilterFunction] = useState<HL.EditingNode>(
      propsFilterFunction
    );
    const updateFilterFunction = useCallback(() => {
      if (HL.nodeIsExecutable(filterFunction)) {
        propsUpdateFilterFunction(filterFunction);
      }
    }, [propsUpdateFilterFunction, filterFunction]);

    const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
    const openColumnSettings = useCallback(() => {
      setColumnSettingsOpen(true);
      // Copy props in to editing function in case props version
      // has changed externally.
      setSelectFunction(propsSelectFunction);
    }, [propsSelectFunction]);

    const [filterOpen, setFilterOpen] = useState(false);
    const openFilter = useCallback(() => {
      setFilterOpen(true);
      // Copy props in to editing function in case props version
      // has changed externally.
      setFilterFunction(propsFilterFunction);
    }, [propsFilterFunction]);

    const selectedNode = useMemo(
      () =>
        // Only use selected node if it's executable (has no voids...)
        // otherwise fall back to the props version.
        // TODO: this isn't really right
        HL.nodeIsExecutable(selectFunction)
          ? Table.getCellValueNode(exampleRowNode, selectFunction)
          : CG.voidNode(),
      [selectFunction, exampleRowNode]
    );
    const {curPanelId, stackIds, handler} = useMemo(
      () => getPanelStacksForType(selectFunction.type, panelId),
      [selectFunction.type, panelId]
    );
    const frame = useMemo(
      () =>
        Table.getExampleFrameFromExampleRow(tableState, colId, exampleRowNode),
      [tableState, exampleRowNode, colId]
    );
    const filteredFrame = useMemo(
      () =>
        Table.getSelectedFrameFromExampleRow(tableState, colId, exampleRowNode),
      [tableState, colId, exampleRowNode]
    );
    const enableGroup = LLReact.useContextBound(Table.enableGroupByCol);
    const disableGroup = LLReact.useContextBound(Table.disableGroupByCol);
    const enableSort = LLReact.useContextBound(Table.enableSortByCol);
    const disableSort = LLReact.useContextBound(Table.disableSortByCol);
    const columnMenuItems: WBMenu.WBMenuOption[] = useMemo(() => {
      let menuItems: WBMenu.WBMenuOption[] = [];
      menuItems.push({
        value: 'settings',
        name: 'Column settings',
        icon: 'configuration',
        onSelect: () => openColumnSettings(),
      });
      // menuItems.push({
      //   value: 'filter',
      //   name: 'Filter',
      //   icon: 'filter',
      //   onSelect: openFilter,
      // });
      if (!isGroupCol && tableState.groupBy.length === 0) {
        menuItems.push({
          value: 'group',
          name: 'Group by',
          icon: 'folder',
          onSelect: async () => {
            const newTableState = await enableGroup(
              tableState,
              colId,
              inputArrayNode
            );
            updateTableState(newTableState);
          },
        });
      } else if (isGroupCol) {
        menuItems.push({
          value: 'ungroup',
          name: 'Ungroup',
          icon: 'folder',
          onSelect: async () => {
            const newTableState = await disableGroup(
              tableState,
              colId,
              inputArrayNode
            );
            updateTableState(newTableState);
          },
        });
      }
      if (Types.canSortType(selectFunction.type)) {
        menuItems = menuItems.concat(
          makeSortingMenuItems(
            tableState,
            colId,
            inputArrayNode,
            enableSort,
            disableSort,
            updateTableState
          )
        );
      }
      if (!isGroupCol) {
        if (menuItems.length > 0) {
          menuItems.push(makeMenuItemDivider('insert-div'));
        }
        menuItems = menuItems.concat([
          {
            value: 'insert-right',
            name: 'Insert 1 right',
            icon: 'next',
            onSelect: () => {
              const newTableState = Table.insertColumnRight(
                tableState,
                colId,
                inputArrayNode
              );
              updateTableState(newTableState);
            },
          },
          {
            value: 'insert-left',
            name: 'Insert 1 left',
            icon: 'previous',
            onSelect: () => {
              const newTableState = Table.insertColumnLeft(
                tableState,
                colId,
                inputArrayNode
              );
              updateTableState(newTableState);
            },
          },
          makeMenuItemDivider('remove-div'),
          {
            value: 'remove',
            name: 'Remove',
            icon: 'delete',
            onSelect: () => {
              const newTableState = Table.removeColumn(tableState, colId);
              updateTableState(newTableState);
            },
          },
        ]);
      }
      return menuItems;
    }, [
      colId,
      isGroupCol,
      enableGroup,
      disableGroup,
      inputArrayNode,
      tableState,
      updateTableState,
      openColumnSettings,
      disableSort,
      enableSort,
      selectFunction.type,
    ]);
    return (
      <S.ColumnHeader>
        <Popup
          basic
          className="wb-table-action-popup"
          on="click"
          open={columnSettingsOpen}
          position="bottom left"
          onOpen={openColumnSettings}
          onClose={SemanticHacks.withIgnoreBlockedClicks(() => {
            setColumnSettingsOpen(false);
            updateSelectFunction();
          })}
          trigger={
            <S.ColumnName>
              {isGroupCol && 'Group by ('}
              {columnName !== '' ? (
                columnName
              ) : (
                <ExpressionView.ExpressionView
                  frame={frame}
                  node={propsSelectFunction}
                />
              )}
              {isGroupCol && ')'}
            </S.ColumnName>
          }
          content={
            columnSettingsOpen && (
              <div>
                <S.ColumnEditorSection>
                  <S.ColumnEditorSectionLabel>
                    Cell expression
                  </S.ColumnEditorSectionLabel>
                  <S.AssignmentWrapper>
                    <ExpressionEditor.ExpressionEditorWrapper>
                      <ExpressionEditor.ExpressionEditor
                        frame={frame}
                        node={selectFunction}
                        updateNode={setSelectFunction}
                        onAccept={updateSelectFunction}
                      />
                    </ExpressionEditor.ExpressionEditorWrapper>
                  </S.AssignmentWrapper>
                  <S.ColumnEditorColumnName>
                    <S.ColumnEditorFieldLabel>
                      Column name:
                    </S.ColumnEditorFieldLabel>
                    <EditableField
                      value={columnName}
                      placeholder={ExpressionView.simpleNodeString(
                        selectFunction
                      )}
                      save={value => updateColumnName(value as string)}
                    />
                  </S.ColumnEditorColumnName>
                </S.ColumnEditorSection>
                <S.ColumnEditorSection>
                  <S.ColumnEditorSectionLabel>
                    Panel type
                  </S.ColumnEditorSectionLabel>
                  <S.PanelNameEditor>
                    <ModifiedDropdown
                      selection
                      search
                      className="clone-report-modal__select"
                      options={stackIds.map(si => ({
                        text: si.displayName,
                        value: si.id,
                      }))}
                      value={curPanelId}
                      onChange={(e, {value}) => updatePanelId(value as string)}
                    />
                  </S.PanelNameEditor>
                  {selectedNode.nodeType !== 'void' && handler != null && (
                    <S.PanelSettings>
                      <PanelComp2
                        input={{path: selectedNode}}
                        inputType={selectFunction.type}
                        loading={false}
                        panelSpec={handler}
                        configMode={true}
                        context={panelContext}
                        config={config}
                        updateConfig={updateConfig}
                        updateContext={updatePanelContext}
                      />
                    </S.PanelSettings>
                  )}
                </S.ColumnEditorSection>
              </div>
            )
          }
        />
        <div>
          {(filterOpen || propsFilterFunction.nodeType !== 'var') && (
            <Popup
              trigger={<S.FilterIcon name="filter" />}
              open={filterOpen}
              on="click"
              position="bottom center"
              onOpen={openFilter}
              onClose={SemanticHacks.withIgnoreBlockedClicks(() => {
                setFilterOpen(false);
                updateFilterFunction();
              })}
              content={
                filterOpen &&
                filteredFrame.v.nodeType !== 'void' && (
                  <div>
                    <S.ColumnEditorSection>
                      <S.ColumnEditorSectionLabel>
                        Filter expression
                      </S.ColumnEditorSectionLabel>
                      <ExpressionEditor.ExpressionEditorWrapper>
                        <ExpressionEditor.ExpressionEditor
                          focusOnMount
                          frame={filteredFrame as {v: CG.Node}}
                          node={filterFunction}
                          updateNode={setFilterFunction}
                        />
                      </ExpressionEditor.ExpressionEditorWrapper>
                    </S.ColumnEditorSection>
                  </div>
                )
              }
            />
          )}
          {Types.canSortType(selectFunction.type) && (
            <SortStateToggle
              {...{
                tableState,
                colId,
                inputArrayNode,
                enableSort,
                disableSort,
                updateTableState,
              }}
            />
          )}
          <WBPopupMenuTrigger options={columnMenuItems}>
            {({anchorRef, setOpen, open}) => (
              <S.EllipsisIcon
                className="column-actions-trigger"
                style={{cursor: 'pointer'}}
                name="overflow"
                ref={anchorRef}
                onClick={() => setOpen(o => !o)}></S.EllipsisIcon>
            )}
          </WBPopupMenuTrigger>
        </div>
      </S.ColumnHeader>
    );
  },
  {id: 'ColumnHeader'}
);

const makeSortingMenuItems = (
  tableState: Table.TableState,
  colId: string,
  inputArrayNode: CG.Node,
  enableSort: (
    ts: Table.TableState,
    colId: string,
    inputArrayNode: CG.Node<Types.Type>,
    asc: boolean
  ) => Promise<Table.TableState>,
  disableSort: (
    ts: Table.TableState,
    colId: string,
    inputArrayNode: CG.Node<Types.Type>
  ) => Promise<Table.TableState>,
  updateTableState: (newTableState: Table.TableState) => void
) => {
  const colSortState = tableState.sort.find(sort => sort.columnId === colId)
    ?.dir;
  const menuItems: WBMenu.WBMenuOption[] = [makeMenuItemDivider('sort-div')];
  if (colSortState !== 'asc') {
    menuItems.push({
      value: 'sort-asc',
      name: 'Sort Asc',
      icon: 'up-arrow',
      onSelect: async () => {
        const newTableState = await enableSort(
          tableState,
          colId,
          inputArrayNode,
          true
        );
        updateTableState(newTableState);
      },
    });
  }

  if (colSortState !== undefined) {
    menuItems.push({
      value: 'sort-remove',
      name: 'Remove Sort',
      icon: 'delete',
      onSelect: async () => {
        const newTableState = await disableSort(
          tableState,
          colId,
          inputArrayNode
        );
        updateTableState(newTableState);
      },
    });
  }

  if (colSortState !== 'desc') {
    menuItems.push({
      value: 'sort-desc',
      name: 'Sort Desc',
      icon: 'down-arrow',
      onSelect: async () => {
        const newTableState = await enableSort(
          tableState,
          colId,
          inputArrayNode,
          false
        );
        updateTableState(newTableState);
      },
    });
  }

  return menuItems;
};

const makeMenuItemDivider = (value: string) => {
  return {
    value,
    disabled: true,
    render: () => (
      <div
        style={{
          marginRight: 12,
          marginLeft: 12,
          borderBottom: '1px solid #888',
        }}
      />
    ),
  };
};

const SortStateToggle: React.FC<{
  tableState: Table.TableState;
  colId: string;
  inputArrayNode: CG.Node;
  enableSort: (
    ts: Table.TableState,
    colId: string,
    inputArrayNode: CG.Node<Types.Type>,
    asc: boolean
  ) => Promise<Table.TableState>;
  disableSort: (
    ts: Table.TableState,
    colId: string,
    inputArrayNode: CG.Node<Types.Type>
  ) => Promise<Table.TableState>;
  updateTableState: (newTableState: Table.TableState) => void;
}> = makeComp(
  ({
    updateTableState,
    enableSort,
    disableSort,
    tableState,
    colId,
    inputArrayNode,
  }) => {
    const colSortState = tableState.sort.find(sort => sort.columnId === colId)
      ?.dir;
    if (colSortState && colSortState === 'desc') {
      return (
        <S.SortedIcon
          name="down-arrow"
          onClick={async e => {
            updateTableState(
              await disableSort(tableState, colId, inputArrayNode)
            );
          }}
        />
      );
    } else if (colSortState && colSortState === 'asc') {
      return (
        <S.SortedIcon
          name="up-arrow"
          onClick={async e => {
            updateTableState(
              await enableSort(tableState, colId, inputArrayNode, false)
            );
          }}
        />
      );
    } else {
      return <></>;
    }
  },
  {id: 'SortStateToggle'}
);

export const Cell: React.FC<{
  rowNode: CG.Node;
  selectFunction: CG.NodeOrVoidNode;
  panelId: string;
  config: any;
  panelContext: any;
  updateConfig(newConfig: any): void;
  updatePanelContext(newContext: any): void;
}> = makeComp(
  ({
    rowNode,
    selectFunction,
    panelId,
    config,
    panelContext,
    updateConfig,
    updatePanelContext,
  }) => {
    const selectedNode = useMemo(
      () => Table.getCellValueNode(rowNode, selectFunction),
      [selectFunction, rowNode]
    );

    const {curPanelId, handler} = useMemo(
      () => getPanelStacksForType(selectedNode.type, panelId),
      [selectedNode.type, panelId]
    );

    const domRef = useRef<HTMLDivElement>(null);
    // Only render when on screen for the first time. Each time selectedNode
    // changes, this behavior resets.
    const shouldRender = useWhenOnScreenAfterNewValue(domRef, selectedNode);

    return (
      <div
        ref={domRef}
        style={{
          ...getPanelStackDims(handler),
          overflowX: 'hidden',
          overflowY: 'hidden',
        }}>
        {curPanelId == null ? (
          <div>-</div>
        ) : (
          shouldRender &&
          selectedNode.nodeType !== 'void' &&
          handler != null && (
            <PanelComp2
              input={{path: selectedNode}}
              inputType={selectFunction.type}
              loading={false}
              panelSpec={handler}
              configMode={false}
              context={panelContext}
              config={config}
              updateConfig={updateConfig}
              updateContext={updatePanelContext}
            />
          )
        )}
      </div>
    );
  },
  {id: 'PanelTableCell'}
);

export const Value: React.FC<{
  valueNode: CG.Node;
  config: any;
  panelContext: any;
  updateConfig(newConfig: any): void;
  updatePanelContext(newContext: any): void;
}> = makeComp(
  ({valueNode, config, panelContext, updateConfig, updatePanelContext}) => {
    console.log('value cell type', valueNode.type);
    const {curPanelId, handler} = useMemo(
      () => getPanelStacksForType(valueNode.type, ''),
      [valueNode.type]
    );

    return (
      <div>
        {curPanelId == null ? (
          <div>No panel for type {Types.toString(valueNode.type)}</div>
        ) : (
          <div>
            {handler != null && (
              <PanelComp2
                input={{path: valueNode}}
                inputType={valueNode.type}
                loading={false}
                panelSpec={handler}
                configMode={false}
                context={panelContext}
                config={config}
                updateConfig={updateConfig}
                updateContext={updatePanelContext}
              />
            )}
          </div>
        )}
      </div>
    );
  },
  {id: 'Value'}
);

type PanelTable = Panel2.PanelProps<typeof inputType, Table.TableState>;

const PanelTable2: React.FC<PanelTable> = props => {
  const {input, updateConfig, updateContext} = props;
  const inputNode = (input.path as any) as CG.Node;

  const preFilterFrame = useMemo(() => Table.getPreFilterFrame(inputNode), [
    inputNode,
  ]);

  const config = useMemo(
    () =>
      props.config == null || props.config.columns == null
        ? Table.emptyTable(inputNode)
        : props.config,
    [props.config, inputNode]
  );

  const rowsNode = useMemo(
    () =>
      Table.getRowsNode(
        config.preFilterFunction,
        config.groupBy,
        config.columnSelectFunctions,
        config.columnFilterFunctions,
        config.sort,
        inputNode
      ),
    [
      config.preFilterFunction,
      config.groupBy,
      config.columnSelectFunctions,
      config.columnFilterFunctions,
      config.sort,
      inputNode,
    ]
  );

  const visibleRowsNode = useMemo(
    () => Table.getPagedRowsNode(config.pageSize, config.page, rowsNode),
    [config.pageSize, config.page, rowsNode]
  );

  const exampleRow = useMemo(() => Table.getExampleRow(rowsNode), [rowsNode]);

  const rowNodesUse = LLReact.useEach(visibleRowsNode as any);

  const rowNodes = useMemo(
    () => (rowNodesUse.loading ? [] : rowNodesUse.result),
    [rowNodesUse.loading, rowNodesUse.result]
  );

  // Only rerender this when not loading
  return useGatedValue(
    config.order.length === 0 ? (
      <Button
        onClick={() => updateConfig(Table.appendColumn(config, inputNode))}>
        Add Column
      </Button>
    ) : (
      <div>
        <div style={{display: 'flex', justifyContent: 'space-between'}}>
          <div>
            <ControlFilter
              frame={preFilterFrame}
              filterFunction={config.preFilterFunction}
              setFilterFunction={newNode =>
                updateConfig(Table.updatePreFilter(config, newNode))
              }
            />
          </div>
          <PageControls
            rowsNode={rowsNode}
            page={config.page}
            pageSize={config.pageSize}
            nextPage={() => updateConfig(Table.nextPage(config))}
            prevPage={() => updateConfig(Table.prevPage(config))}
          />
        </div>
        <STable>
          <STable.Header>
            <STable.Row>
              {config.groupBy
                .concat(Table.getColumnRenderOrder(config))
                .map((colId, colIndex) => (
                  <STable.HeaderCell key={colId}>
                    <ColumnHeader
                      isGroupCol={colIndex < config.groupBy.length}
                      tableState={config}
                      inputArrayNode={inputNode}
                      exampleRowNode={exampleRow}
                      columnName={config.columnNames[colId]}
                      selectFunction={config.columnSelectFunctions[colId]}
                      filterFunction={config.columnFilterFunctions[colId]}
                      colId={colId}
                      panelId={config.columns[colId].panelId}
                      config={config.columns[colId].panelConfig}
                      panelContext={props.context}
                      updateConfig={newConfig =>
                        updateConfig(
                          Table.updateColumnPanelConfig(
                            config,
                            colId,
                            newConfig
                          )
                        )
                      }
                      updatePanelContext={updateContext}
                      updateTableState={props.updateConfig}
                      updateColumnName={newName =>
                        updateConfig(
                          Table.updateColumnName(config, colId, newName)
                        )
                      }
                      updateSelectFunction={newNode =>
                        updateConfig(
                          Table.updateColumnSelect(config, colId, newNode)
                        )
                      }
                      updateFilterFunction={newNode =>
                        updateConfig(
                          Table.updateColumnFilter(config, colId, newNode)
                        )
                      }
                      updatePanelId={newPanelId =>
                        updateConfig(
                          Table.updateColumnPanelId(config, colId, newPanelId)
                        )
                      }
                    />
                  </STable.HeaderCell>
                ))}
            </STable.Row>
          </STable.Header>
          <STable.Body>
            {rowNodes.map((row, rowIndex) => (
              <STable.Row key={rowIndex}>
                {config.groupBy.length > 0 && (
                  <STable.Cell
                    // override a style that we set in semantic that shouldn't apply here
                    // .file-browser td {max-width}
                    style={{maxWidth: 'none'}}
                    key="group-col">
                    <Value
                      // Warning: not memoized
                      valueNode={Op.opGetTag({
                        obj: rowNodesUse.result[rowIndex] as any,
                      })}
                      config={{}}
                      panelContext={props.context}
                      updateConfig={newConfig =>
                        updateConfig(
                          Table.updateColumnPanelConfig(
                            config,
                            config.groupBy[0],
                            newConfig
                          )
                        )
                      }
                      updatePanelContext={updateContext}
                    />
                  </STable.Cell>
                )}
                {Table.getColumnRenderOrder(config).map(colId => (
                  <STable.Cell
                    // override a style that we set in semantic that shouldn't apply here
                    // .file-browser td {max-width}
                    style={{maxWidth: 'none'}}
                    key={colId}>
                    <Cell
                      rowNode={row}
                      selectFunction={config.columnSelectFunctions[colId]}
                      panelId={config.columns[colId].panelId}
                      config={config.columns[colId].panelConfig}
                      panelContext={props.context}
                      updateConfig={newConfig =>
                        updateConfig(
                          Table.updateColumnPanelConfig(
                            config,
                            colId,
                            newConfig
                          )
                        )
                      }
                      updatePanelContext={updateContext}
                    />
                  </STable.Cell>
                ))}
              </STable.Row>
            ))}
          </STable.Body>
        </STable>
      </div>
    ),
    o => !rowNodesUse.loading
  );
};

export const Spec: Panel2.PanelSpec = {
  id: 'table2',
  Component: PanelTable2,
  inputType,
};
